import { atTime, formatInZone, timeLabel, toServiceDate } from '../services/reservation-time.util';
import { TemporaryClosure } from './serving-hours.util';
import { CLOSED_STATUSES, Reservation } from '../models/reservation';
import { UserRole } from './user-role.util';

/**
 * Whether the restaurant is open right now, as restaurant-service works it out
 * (FNF-1612): `GET /v2/restaurants/hours/status`. The dashboard shows it on the
 * Restaurant Status card and, while a temporary closure is in force, as a chip
 * in the header.
 */
export type OpenStatusReason = 'unavailable' | 'temporarily_closed' | 'holiday' | 'regular';

export interface OpenStatus {
  /** null when no store hours are set and nothing else decides. */
  is_open: boolean | null;
  reason: OpenStatusReason | null;
  /** The closure's reason, or the closed date's note. */
  note: string | null;
  /** When open: the end of the current opening, with the restaurant's offset. */
  closes_at: string | null;
  /** When closed: the next opening within a week. */
  next_open_at: string | null;
}

export interface HoursStatus {
  open_status: OpenStatus;
  /** Only while it is in force. */
  temporary_closure: TemporaryClosure | null;
}

export type StatusTone = 'open' | 'closed' | 'paused' | 'unknown';

/** Who may close the restaurant for now and reopen it; the API agrees. */
const CLOSURE_ROLES: string[] = [
  UserRole.superAdmin,
  UserRole.restaurantAdmin,
  UserRole.restaurantManager,
  UserRole.platformSupport,
];

export function canManageClosure(userRole: string): boolean {
  return CLOSURE_ROLES.includes(userRole);
}

/**
 * "6:00 PM" for a time later today on the restaurant's clock, "Mon 11:00 AM"
 * within the week, "Nov 12, 6:00 PM" beyond.
 */
export function whenLabel(iso: string, timeZone: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const days = dayDiff(toServiceDate(now, timeZone), toServiceDate(at, timeZone));
  if (days === 0) {
    return timeLabel(at, timeZone);
  }
  if (days > 0 && days < 7) {
    return `${formatInZone(at, timeZone, { weekday: 'short' })} ${timeLabel(at, timeZone)}`;
  }
  return formatInZone(at, timeZone, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** The card's one line, e.g. "Temporarily closed · back at 6:00 PM · Power cut". */
export function statusLine(
  status: HoursStatus,
  timeZone: string,
  now: Date = new Date(),
): { tone: StatusTone; text: string } {
  const s = status.open_status;
  const when = (iso: string | null) => (iso ? whenLabel(iso, timeZone, now) : null);
  if (s.reason === 'temporarily_closed') {
    // "Back at" is when guests can come in: the server's next opening, which
    // is the closure's end only if the hours are open then (found end to
    // end: a closure ending at 2 AM read "back at 2 AM" for a 6 AM opening).
    const until = status.temporary_closure?.closed_until;
    return {
      tone: 'paused',
      text: join(
        'Temporarily closed',
        s.next_open_at
          ? `back at ${when(s.next_open_at)}`
          : until
            ? `until ${when(until)}`
            : 'until you reopen',
        s.note,
      ),
    };
  }
  if (s.reason === 'unavailable') {
    return { tone: 'closed', text: 'Not available to guests: the restaurant is deactivated' };
  }
  if (s.is_open === null) {
    return { tone: 'unknown', text: 'No opening hours set yet' };
  }
  if (s.is_open) {
    return {
      tone: 'open',
      text: join('Open now', s.closes_at ? `closes ${when(s.closes_at)}` : 'open all week'),
    };
  }
  const opens = s.next_open_at ? `opens ${when(s.next_open_at)}` : null;
  if (s.reason === 'holiday') {
    return {
      tone: 'closed',
      text: join(s.note ? `Closed today: ${s.note}` : 'Closed today', opens),
    };
  }
  return { tone: 'closed', text: join('Closed', opens) };
}

/** The header chip, or null when there is no closure in force. */
export function closureChipLabel(
  status: HoursStatus | null,
  timeZone: string,
  now: Date = new Date(),
): string | null {
  const closure = status?.temporary_closure;
  if (!closure) {
    return null;
  }
  return closure.closed_until
    ? `Closed until ${whenLabel(closure.closed_until, timeZone, now)}`
    : 'Closed until you reopen';
}

export type ClosurePreset = 'hour' | 'today' | 'next_opening' | 'reopen';

export const CLOSURE_PRESETS: { value: ClosurePreset; label: string }[] = [
  { value: 'hour', label: '1 hour' },
  { value: 'today', label: 'Rest of today' },
  { value: 'next_opening', label: 'Until next opening' },
  { value: 'reopen', label: 'Until I reopen' },
];

/**
 * Which presets make sense now. "Until next opening" closes what is left of
 * the current opening, so it needs the restaurant to be open.
 */
export function availablePresets(status: HoursStatus | null): ClosurePreset[] {
  const open = !!status?.open_status.is_open && !!status.open_status.closes_at;
  return CLOSURE_PRESETS.map((p) => p.value).filter((p) => p !== 'next_opening' || open);
}

/**
 * The `until` to send for a preset; null closes until reopened.
 * - 1 hour: an hour from now.
 * - Until next opening: the end of the current opening; the weekly hours keep
 *   it closed from there to the next one.
 * - Rest of today: midnight on the restaurant's clock, or the end of the
 *   current opening when that runs past midnight (a 5 PM – 2 AM night).
 */
export function presetUntil(
  preset: ClosurePreset,
  status: HoursStatus | null,
  timeZone: string,
  now: Date = new Date(),
): string | null {
  const closesAt = status?.open_status.is_open ? status.open_status.closes_at : null;
  switch (preset) {
    case 'hour':
      return new Date(now.getTime() + 60 * 60_000).toISOString();
    case 'next_opening':
      return closesAt ? new Date(closesAt).toISOString() : null;
    case 'today': {
      const midnight = atTime(nextDay(toServiceDate(now, timeZone)), '00:00', timeZone);
      const end = closesAt && new Date(closesAt) > midnight ? new Date(closesAt) : midnight;
      return end.toISOString();
    }
    case 'reopen':
      return null;
  }
}

/**
 * The span a closure would cover, for counting the bookings already in it:
 * from now to `until`, or, until reopened, to the end of today on the
 * restaurant's clock. Its service dates, to read the book by, are capped
 * at three.
 */
export function closureWindow(
  until: string | null,
  timeZone: string,
  now: Date = new Date(),
): { from: Date; to: Date; dates: string[] } {
  const to = until
    ? new Date(until)
    : atTime(nextDay(toServiceDate(now, timeZone)), '00:00', timeZone);
  const dates: string[] = [];
  const last = toServiceDate(new Date(to.getTime() - 1), timeZone);
  for (let d = toServiceDate(now, timeZone); d <= last && dates.length < 3; d = nextDay(d)) {
    dates.push(d);
  }
  return { from: now, to, dates };
}

/**
 * Bookings a closure doesn't cancel (reservation-service leaves them):
 * live reservations starting inside the window. The earliest one's date
 * is where "Open the book" goes.
 */
export function bookingsInWindow(
  rows: Reservation[],
  from: Date,
  to: Date,
): { count: number; firstDate: string | null } {
  const inside = rows
    .filter(
      (r) =>
        r.kind === 'reservation' &&
        !CLOSED_STATUSES.includes(r.status) &&
        new Date(r.startAt) >= from &&
        new Date(r.startAt) < to,
    )
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
  return { count: inside.length, firstDate: inside[0]?.serviceDate ?? null };
}

function join(...parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(' · ');
}

function nextDay(serviceDate: string): string {
  const [y, m, d] = serviceDate.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

function dayDiff(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
}
